import { Column, Bar, Pie, Line, DualAxes } from '@ant-design/plots';
import { Row, Col, Card, Typography, notification, Spin, Skeleton, Statistic, DatePicker, Button, Space, Select } from 'antd';
import { useHttp } from '../../../utils/DataHendler';
import { useEffect, useState } from 'react';
import { UserOutlined, EnvironmentOutlined, CheckCircleOutlined, TeamOutlined, CalendarOutlined, ReloadOutlined, FilterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Title } = Typography;
const { RangePicker } = DatePicker;



// Component for Most Active Security Personnel
export const MostActiveSecurity = ({ data = [], loading = false }) => {
  if (loading) {
    return <Skeleton active paragraph={{ rows: 8 }} />;
  }

  const config = {
    data: data,
    xField: 'title',
    yField: 'value',
    style: {
      fill: ({ value }) => {
        if (value > 500) return '#52c41a'; // Green for high activity
        if (value > 400) return '#1890ff'; // Blue for medium activity
        return '#faad14'; // Orange for lower activity
      },
    },
    label: {
      text: (d) => `${d.value}`,
      position: 'top',
      style: {
        fontWeight: 'bold',
      },
    },
    tooltip: {
      title: 'title',
      items: [
        {
          field: 'value',
          name: 'Inspections',
        },
      ],
    },
    legend: false,
    axis: {
      x: {
        label: {
          style: {
            fontSize: 10,
            transform: 'rotate(-45)',
          },
        },
      },
    },
  };
  return <Column {...config} />;
};

// Component for Most Inspected Locations
// export const MostInspectedLocations = ({ data = [], loading = false }) => {
//   if (loading) {
//     return <Skeleton active paragraph={{ rows: 8 }} />;
//   }

//   const config = {
//     data: data,
//     angleField: 'value',
//     colorField: 'title',
//     radius: 0.8,
//     innerRadius: 0.5,
//     label: {
//       text: (d) => `${d.value}`,
//       position: 'outside',
//       style: {
//         fontWeight: 'bold',
//         fontSize: 11,
//       },
//     },
//     tooltip: {
//       title: 'title',
//       items: [
//         {
//           field: 'value',
//           name: 'Inspections',
//         },
//       ],
//     },
//     legend: {
//       position: 'bottom',
//       layout: 'horizontal',
//     },
//     style: {
//       lineWidth: 2,
//       stroke: '#fff',
//     },
//   };
//   return <Pie {...config} />;
// };


// Component for Weekly Person Inspection Trend
export const WeeklyPersonInspection = ({ data = [], loading = false }) => {
  if (loading) {
    return <Skeleton active paragraph={{ rows: 8 }} />;
  }

  // Convert date format from DD/MM/YYYY to proper Date object
  const processedData = data.map(item => ({
    ...item,
    Date: dayjs(item.Date, 'DD/MM/YYYY').toDate()
  }));

  const config = {
    data: processedData,
    interaction: {
      brushFilter: true,
    },
    colorField: 'series',
    yField: 'value',
    xField: (d) => d.Date,
    axis: {
      x: {
        tickStroke: '#cdcdcd',
        gridStroke: '#efefef',
        labelAutoRotate: false,
        label: {
          formatter: (text) => {
            return dayjs(text).format('DD/MM');
          },
        },
      },
      y: {
        tickStroke: '#cdcdcd',
        gridStroke: '#efefef',
        title: {
          text: 'Daily Inspections',
        },
      },
    },
    tooltip: {
      title: (d) => dayjs(d.Date).format('DD/MM/YYYY'),
      items: [
        {
          field: 'value',
          name: 'Inspections',
        },
        {
          field: 'series',
          name: 'Security Personnel',
        },
      ],
    },
    legend: {
      position: 'bottom',
      layout: 'horizontal',
    },
    smooth: true,
  };

  return <Line {...config} />;
};

// Component for Least Inspected Locations
export const LeastInspectedLocations = ({ data = [], loading = false }) => {
  if (loading) {
    return <Skeleton active paragraph={{ rows: 8 }} />;
  }

  const config = {
    data: data,
    xField: 'value',
    yField: 'title',
    style: {
      fill: ({ value }) => {
        if (value < 200) return '#ff4d4f'; // Red for very low inspections
        if (value < 220) return '#faad14'; // Orange for low inspections
        return '#52c41a'; // Green for acceptable inspections
      },
    },
    label: {
      text: (d) => `${d.value}`,
      position: 'right',
      style: {
        fontWeight: 'bold',
      },
    },
    tooltip: {
      title: 'title',
      items: [
        {
          field: 'value',
          name: 'Inspections',
        },
      ],
    },
    legend: false,
  };
  return <Bar {...config} />;
};

// Component for Least Active Security Personnel
export const LeastActiveSecurity = ({ data = [], loading = false }) => {
  if (loading) {
    return <Skeleton active paragraph={{ rows: 8 }} />;
  }

  const config = {
    data: data,
    xField: 'title',
    yField: 'value',
    style: {
      fill: ({ value }) => {
        if (value < 50) return '#ff4d4f'; // Red for very low activity
        if (value < 350) return '#faad14'; // Orange for low activity
        return '#1890ff'; // Blue for acceptable activity
      },
    },
    label: {
      text: (d) => `${d.value}`,
      position: 'top',
      style: {
        fontWeight: 'bold',
      },
    },
    tooltip: {
      title: 'title',
      items: [
        {
          field: 'value',
          name: 'Inspections',
        },
      ],
    },
    legend: false,
    axis: {
      x: {
        label: {
          style: {
            fontSize: 10,
            transform: 'rotate(-45)',
          },
        },
      },
    },
  };
  return <Column {...config} />;
};

// Component for Daily Top Locations Trend
export const DailyTopLocations = ({ data = [], loading = false }) => {
  if (loading) {
    return <Skeleton active paragraph={{ rows: 8 }} />;
  }

  // Convert date format from DD/MM/YYYY to proper Date object
  const processedData = data.map(item => ({
    ...item,
    Date: dayjs(item.Date, 'DD/MM/YYYY').toDate()
  }));

  const config = {
    data: processedData,
    interaction: {
      brushFilter: true,
    },
    colorField: 'series',
    yField: 'value',
    xField: (d) => d.Date,
    axis: {
      x: {
        labelAutoRotate: false,
        label: {
          formatter: (text) => {
            return dayjs(text).format('DD/MM');
          },
        },
      },
      y: {
        title: {
          text: 'Inspections Count',
        },
      },
    },
    tooltip: {
      title: (d) => dayjs(d.Date).format('DD/MM/YYYY'),
      items: [
        {
          field: 'value',
          name: 'Inspections',
        },
        {
          field: 'series',
          name: 'Location',
        },
      ],
    },
    legend: {
      position: 'bottom',
      layout: 'horizontal',
    },
    smooth: true,
  };

  return <Line {...config} />;
};

// Main Report Structure Component
export const ReportStructure = () => {

    const [api, contextHolder] = notification.useNotification();
    const [loading, setLoading] = useState(false);
    const [loadingWeekly, setLoadingWeekly] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [dateRange, setDateRange] = useState(null);
    const [selectedUsers, setSelectedUsers] = useState([]); // Multiple user selection
    const [usersList, setUsersList] = useState([]); // Available users from API
    
    const [patrolData, setPatrolData] = useState({
      summary: {
        total_active_security: 0,
        total_inspected_locations: 0,
        total_inspections: 0,
        period_start: null,
        period_end: null,
        total_locations: 0,
        total_security_staff: 0,
        date_filter: null
      },
      statistics: {
        most_active_security: [],
        most_inspected_locations: [],
        least_inspected_locations: [],
        least_active_security: [],
        daily_top_locations: []
      }
    });

    const [weeklyData, setWeeklyData] = useState({
      selected_users: [],
      period: {
        start_date: null,
        end_date: null,
        description: ""
      },
      summary: {
        total_selected_users: 0,
        selection_method: "",
        period_total_inspections: 0,
        average_inspections_per_user: "0.00"
      },
      trend_data: []
    });

    const { getData } = useHttp();
    
    useEffect(() => {
        getPatrolStatistics();
        getUsersList();
        getWeeklyStatistics(null, selectedUsers);
    }, []);

    // Fetch users list for selection
    const getUsersList = async () => {
      setLoadingUsers(true);
      try {
        const response = await getData(`/users/lookup`, { params: { limit: 50, role_code: 'R-003' } }, true);
        const responseData = response.data || response;
        
        if (responseData?.data) {
          setUsersList(responseData.data);
        }
        
        setLoadingUsers(false);
      } catch (error) {
        console.log(error);
        setLoadingUsers(false);
        
        api.error({
          message: "Error",
          description: "Failed to fetch users list",
        });
      }
    };
  
    const getPatrolStatistics = async (dateFilter = null) => {
      setLoading(true);

      try {
        let params = {};
        
        // Add date filter if provided
        if (dateFilter && dateFilter.length === 2) {
          params['date[]'] = [
            dateFilter[0].format('YYYY-MM-DD'),
            dateFilter[1].format('YYYY-MM-DD')
          ];
        }

        const response = await getData(`/statistic/patrol`, { params }, true);
        const responseData = response.data || response;

        if (responseData) {
          setPatrolData(responseData);
        }

        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
        
        api.error({
          message: "Error",
          description: "Failed to fetch patrol statistics data",
        });
      }
    };

    const getWeeklyStatistics = async (dateFilter = null, userIds = []) => {
      setLoadingWeekly(true);

      try {
        let params = {};
        
        // Add date filter if provided
        if (dateFilter && dateFilter.length === 2) {
          params['date[]'] = [
            dateFilter[0].format('YYYY-MM-DD'),
            dateFilter[1].format('YYYY-MM-DD')
          ];
        }

        // Add user IDs filter if provided
        if (userIds && userIds.length > 0) {
          params['user_ids[]'] = userIds;
        }

        const response = await getData(`/statistic/weekly`, { params }, true);
        const responseData = response.data || response;

        if (responseData) {
          setWeeklyData(responseData);
        }

        setLoadingWeekly(false);
      } catch (error) {
        console.log(error);
        setLoadingWeekly(false);
        
        api.error({
          message: "Error",
          description: "Failed to fetch weekly statistics data",
        });
      }
    };

    const handleDateRangeChange = (dates) => {
      setDateRange(dates);
      getPatrolStatistics(dates);
      getWeeklyStatistics(dates, selectedUsers);
    };

    const handleResetFilter = () => {
      setDateRange(null);
      setSelectedUsers([]);
      getPatrolStatistics(null);
      getWeeklyStatistics(null, []);
    };

    const handleUsersChange = (userIds) => {
      setSelectedUsers(userIds);
      getWeeklyStatistics(dateRange, userIds);
    };

    const formatDate = (dateString) => {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('id-ID', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };



  return (
    <>
    {contextHolder}
    <div style={{ padding: '20px', backgroundColor: '#f5f5f5' }}>
      <Title level={2} style={{ textAlign: 'center', marginBottom: '20px' }}>
        Patrol Statistics Dashboard
        {patrolData.summary.period_start && patrolData.summary.period_end && (
          <div style={{ fontSize: '14px', fontWeight: 'normal', color: '#666', marginTop: '8px' }}>
            Period: {formatDate(patrolData.summary.period_start)} to {formatDate(patrolData.summary.period_end)}
          </div>
        )}
      </Title>
      
      {/* Date Range Filter */}
      <div 
        style={{ 
          marginBottom: '30px', 
          padding: '16px', 
          backgroundColor: '#fff', 
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}
      >
        <Space size="middle" wrap>
          <CalendarOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
          <span style={{ fontWeight: '500', color: '#666' }}>Filter by Date Range:</span>
          <RangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            placeholder={['Start Date', 'End Date']}
            allowClear
            style={{ width: '280px' }}
            format="YYYY-MM-DD"
          />
          <Button 
            icon={<ReloadOutlined />} 
            onClick={handleResetFilter}
            type="default"
            style={{ marginLeft: '8px' }}
          >
            Reset
          </Button>
        </Space>
      </div>
      
      {/* Summary Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '30px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Statistic
              title="Total Inspections"
              value={patrolData.summary.total_inspections}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              loading={loading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Statistic
              title="Active Security Staff"
              value={patrolData.summary.total_active_security}
              prefix={<UserOutlined style={{ color: '#1890ff' }} />}
              loading={loading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Statistic
              title="Inspected Locations"
              value={patrolData.summary.total_inspected_locations}
              prefix={<EnvironmentOutlined style={{ color: '#faad14' }} />}
              loading={loading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Statistic
              title="Total Security Staff"
              value={patrolData.summary.total_security_staff}
              prefix={<TeamOutlined style={{ color: '#722ed1' }} />}
              loading={loading}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]}>
        {/* Most Active Security */}
        <Col xs={24} lg={12}>
          <Card 
            title="Top 10 Active Guard Personnel" 
            bordered={false}
            style={{ 
              height: '450px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
            bodyStyle={{ height: '370px' }}
          >
            <MostActiveSecurity 
              data={patrolData.statistics.most_active_security} 
              loading={loading}
            />
          </Card>
        </Col>

        {/* Weekly Security Trend */}
        <Col xs={24} lg={12}>
          <Card
            title={`Weekly Security Trend (${weeklyData.summary.total_selected_users} Personnel)`}
            bordered={false}
            style={{ 
              height: '450px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
            bodyStyle={{ height: '370px' }}
            extra={
              <Space size="middle" direction="vertical" style={{ alignItems: 'flex-end' }}>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {weeklyData.period.description}
                </div>
                <Select
                  mode="multiple"
                  value={selectedUsers}
                  onChange={handleUsersChange}
                  placeholder="Select users"
                  style={{ width: 200 }}
                  size="small"
                  loading={loadingWeekly || loadingUsers}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                  options={usersList.map(user => ({
                    value: user.id,
                    label: user.name || user.username,
                    key: user.id
                  }))}
                  maxTagCount="responsive"
                />
              </Space>
            }
          >
            <WeeklyPersonInspection 
              data={weeklyData.trend_data} 
              loading={loadingWeekly}
            />
          </Card>
        </Col>

        {/* Least Inspected Locations */}
        <Col xs={24} lg={12}>
          <Card 
            title="Least Inspected Locations" 
            bordered={false}
            style={{ 
              height: '450px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
            bodyStyle={{ height: '370px' }}
          >
            <LeastInspectedLocations 
              data={patrolData.statistics.least_inspected_locations} 
              loading={loading}
            />
          </Card>
        </Col>

        {/* Least Active Security */}
        <Col xs={24} lg={12}>
          <Card 
            title="Least Active Security Personnel" 
            bordered={false}
            style={{ 
              height: '450px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
            bodyStyle={{ height: '370px' }}
          >
            <LeastActiveSecurity 
              data={patrolData.statistics.least_active_security} 
              loading={loading}
            />
          </Card>
        </Col>
      </Row>

      {/* Daily Top Locations Trend - Full Width */}
      <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
        <Col xs={24}>
          <Card 
            title="Daily Top Locations Inspection Trend" 
            bordered={false}
            style={{ 
              height: '500px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
            bodyStyle={{ height: '420px' }}
          >
            <DailyTopLocations 
              data={patrolData.statistics.daily_top_locations} 
              loading={loading}
            />
          </Card>
        </Col>
      </Row>
    </div>
    </>
  );
};